/**
 * `React.lazy` that survives a deploy (7HAI-1224).
 *
 * THE PROBLEM. Every route in this app is `lazy(() => import(...))` and every
 * chunk filename carries a content hash. A deploy changes those hashes and
 * removes the old files, so a tab opened BEFORE the deploy is holding
 * references to files the server no longer has. The next navigation — logging
 * in is the first one most people make — asks for a chunk that 404s.
 *
 * Nothing caught that. `React.lazy` rejects, the rejection reaches
 * `Sentry.ErrorBoundary` in main.tsx, and its fallback is a static paragraph
 * with no way forward. The app is dead until a hard navigation, which is why
 * the only known workaround was editing the URL by hand.
 *
 * THE FIX IS A RELOAD, ONCE. A stale chunk is not an error to report, it is an
 * out-of-date page: the server has the right code and the tab does not, so
 * fetching the page again resolves it completely. What matters is the "once".
 *
 * WHY sessionStorage AND NOT A MODULE VARIABLE: the reload throws away all
 * module state by definition, so an in-memory guard cannot survive to prevent
 * the second attempt — it would loop forever. sessionStorage survives a reload
 * and dies with the tab, which is exactly the lifetime the guard needs.
 *
 * WHY IT MUST NOT LOOP: if the chunk is still missing after one reload, the
 * deploy is broken in a way reloading cannot fix — a half-uploaded dist, a CDN
 * serving a stale index. Spinning on that hides it from everyone; failing shows
 * it to somebody who can act. So the second failure is allowed through to the
 * error boundary.
 */
import { lazy } from "react";
import type { ComponentType, LazyExoticComponent } from "react";

/** sessionStorage key recording that this tab has already reloaded once. */
export const RELOAD_FLAG = "bid:chunk-reload";

/**
 * True when an error is a missing/failed module chunk rather than a bug.
 *
 * The engines word this differently and all three are in scope, so matching one
 * of them would leave the recovery silently inoperative on somebody's browser:
 *
 *   Chrome   Failed to fetch dynamically imported module: …
 *   Firefox  error loading dynamically imported module
 *   Safari   Importing a module script failed.
 *   Vite     Unable to preload CSS for …
 *
 * Deliberately NARROW. A `TypeError: Cannot read properties of undefined` is a
 * real bug, and reloading past it would convert a visible crash into an
 * invisible reload — the worst possible trade, because nobody would ever
 * report it.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const message = String((error as { message?: unknown }).message ?? "");
  const name = String((error as { name?: unknown }).name ?? "");
  if (name === "ChunkLoadError") return true;
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /unable to preload css/i.test(message)
  );
}

/** sessionStorage access that cannot itself throw.
 *
 *  Private-mode Safari and some embedded webviews throw on access rather than
 *  returning null. A recovery path that crashes while recovering is worse than
 *  no recovery, so every use is guarded — and a failure degrades to "no reload",
 *  which is exactly today's behaviour. */
function flagRead(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) !== null;
  } catch {
    return true; // cannot record a reload => must not start one
  }
}

function flagWrite(): void {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    /* nothing to do — the read guard above already refuses in this case */
  }
}

function flagClear(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

type Importer<T> = () => Promise<{ default: T }>;

/**
 * `React.lazy`, but a chunk that has vanished under a deploy reloads the page
 * once instead of killing the app.
 *
 * Returns the same shape `lazy()` does, so call sites only change the function
 * they call.
 */
// Constrained to `ComponentType<object>` rather than React's own
// `ComponentType<any>`: every call site is a route rendered as <Page />, so
// props are always {}, and `object` accepts that without importing `any` into
// a file whose whole job is to be trustworthy.
export function lazyWithRetry<T extends ComponentType<object>>(
  importer: Importer<T>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await importer();
      // A success means this tab is running code the server still has, so any
      // earlier reload is spent. Without clearing it, the SECOND deploy a user
      // lives through would get no recovery at all.
      flagClear();
      return mod;
    } catch (error) {
      if (!isChunkLoadError(error) || flagRead()) throw error;

      flagWrite();
      // `reload()` does not stop execution, and the pending promise must not
      // resolve into a render while the document is being replaced. Never
      // settling is correct here: the page is going away.
      window.location.reload();
      return new Promise<never>(() => {});
    }
  });
}
